import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { getStoredToken, setStoredToken, clearStoredToken } from '../utils/tokenStorage';

const AuthContext = createContext(null);

// JWT 토큰에서 만료 시간 추출
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // ms 변환
  } catch {
    return null;
  }
}

// 토큰 만료까지 남은 시간(ms)
function getTimeUntilExpiry(token) {
  const expiry = getTokenExpiry(token);
  if (!expiry) return 0;
  return expiry - Date.now();
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  // 토큰 갱신 함수
  const refreshToken = useCallback(async () => {
    const currentToken = getStoredToken();
    if (!currentToken) return;

    try {
      const res = await api.post('/auth/refresh');
      if (res.data.success && res.data.token) {
        setStoredToken(res.data.token);
        setToken(res.data.token);
        scheduleTokenRefresh(res.data.token);
      }
    } catch {
      // 갱신 실패 시 api interceptor에서 처리
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 토큰 만료 전 자동 갱신 스케줄링
  const scheduleTokenRefresh = useCallback((tok) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!tok) return;

    const timeUntilExpiry = getTimeUntilExpiry(tok);
    if (timeUntilExpiry <= 0) return;

    // 만료 30분 전에 갱신 (최소 10초 후)
    const refreshIn = Math.max(timeUntilExpiry - 30 * 60 * 1000, 10000);

    refreshTimerRef.current = setTimeout(() => {
      refreshToken();
    }, refreshIn);
  }, [refreshToken]);

  const checkAuth = useCallback(async () => {
    const t = getStoredToken();
    if (!t) { setUser(null); setToken(null); setLoading(false); return; }

    // 토큰 만료 확인
    const timeLeft = getTimeUntilExpiry(t);
    if (timeLeft <= 0) {
      clearStoredToken();
      setUser(null); setToken(null); setLoading(false);
      return;
    }

    try {
      const res = await api.get('/auth/me');
      setUser(res.data.user);
      setToken(t);
      scheduleTokenRefresh(t);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        clearStoredToken();
        setUser(null); setToken(null);
      }
    } finally { setLoading(false); }
  }, [scheduleTokenRefresh]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // 다른 탭에서 로그아웃/로그인 감지
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (!e.newValue) {
          setUser(null); setToken(null);
        } else {
          checkAuth();
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkAuth]);

  // 타이머 정리
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      const { token: t, user: u } = res.data;
      setStoredToken(t);
      setToken(t); setUser(u);
      scheduleTokenRefresh(t);
      return { success: true, user: u };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || '로그인에 실패했습니다.' };
    }
  };

  const register = async (data) => {
    try {
      const res = await api.post('/auth/register', data);
      const { token: t, user: u } = res.data;
      setStoredToken(t);
      setToken(t); setUser(u);
      scheduleTokenRefresh(t);
      return { success: true, user: u };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || '가입에 실패했습니다.' };
    }
  };

  const logout = () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    clearStoredToken();
    setToken(null); setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, register, checkAuth, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};
